import React, { useCallback, useMemo, useState } from 'react';
import { View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import * as DocumentPicker from 'expo-document-picker';
import Toast from 'react-native-root-toast';

import { useAppContext } from '../../../common/utils/context';
import getGraphQLErrorMessage from '../../../common/utils/errors/getGraphQLErrorMessage';
import { getMissingTicketRepairReportFields } from '../../../common/utils/ticket/canResolveTicket';
import { isRemoteTicketPhotoUrl } from '../../../common/utils/ticket/ticketPhotoUrls';
import { Button, ErrorCallout, Text, TextInput } from '../../components';
import { DateSelectorInput } from '../../components/input';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { s3upload } from '../../utils/fileUpload';
import {
	normalizeTicketRepairReportAsset,
	TICKET_REPAIR_REPORT_PICKER_MIME_TYPES,
	validateTicketRepairReportAssetSize,
} from '../../utils/ticketRepairReportFile';

const TicketRepairReportDialog = ({ ticket, modifyItem, closeDialog }) => {
	const styles = useThemedStyles(createStyles);
	const { getServerEndpoint } = useAppContext();
	const [values, setValues] = useState({
		repairWorkCarriedOut: ticket.repairWorkCarriedOut || '',
		repairPartsUsed: ticket.repairPartsUsed || '',
		repairOutcome: ticket.repairOutcome || '',
		repairCompletedByName: ticket.repairCompletedByName || '',
		repairCompletedAt: ticket.repairCompletedAt || '',
	});
	const [selectedReport, setSelectedReport] = useState(null);
	const [pendingReportUrl, setPendingReportUrl] = useState('');
	const [isSaving, setIsSaving] = useState(false);
	const [formError, setFormError] = useState('');

	const updateValue = useCallback(field => value => {
		setValues(currentValues => ({ ...currentValues, [field]: value }));
		setFormError('');
	}, []);

	const missingRequiredFields = useMemo(() => (
		getMissingTicketRepairReportFields({
			...ticket,
			...values,
			repairReportUrl: pendingReportUrl || ticket.repairReportUrl,
		}).filter(field => field !== 'Uploaded report' || !selectedReport)
	), [pendingReportUrl, selectedReport, ticket, values]);

	const chooseReport = useCallback(async () => {
		setFormError('');
		try {
			const result = await DocumentPicker.getDocumentAsync({
				type: TICKET_REPAIR_REPORT_PICKER_MIME_TYPES,
				copyToCacheDirectory: true,
				multiple: false,
			});

			if (result?.canceled || result?.cancelled || !result?.assets?.[0]) {
				return;
			}

			const selectedAsset = normalizeTicketRepairReportAsset(result.assets[0]);
			await validateTicketRepairReportAssetSize(selectedAsset);
			setSelectedReport(selectedAsset);
			setPendingReportUrl('');
		}
		catch (error) {
			setFormError(error?.message || 'Unable to open the file picker');
		}
	}, []);

	const saveReport = useCallback(async () => {
		if (missingRequiredFields.length || isSaving) {
			return;
		}

		setIsSaving(true);
		setFormError('');
		try {
			let repairReportUrl = pendingReportUrl || ticket.repairReportUrl;
			if (selectedReport && !pendingReportUrl) {
				repairReportUrl = await s3upload(
					selectedReport,
					null,
					'files/ticketRepairReports/',
					getServerEndpoint,
					undefined,
					selectedReport.extension,
					selectedReport.mimeType,
				);
				if (!isRemoteTicketPhotoUrl(repairReportUrl)) {
					throw new Error('Unable to upload the repair report');
				}
				setPendingReportUrl(repairReportUrl);
			}

			const result = await modifyItem({
				id: ticket.id,
				repairWorkCarriedOut: values.repairWorkCarriedOut.trim(),
				repairPartsUsed: values.repairPartsUsed.trim(),
				repairOutcome: values.repairOutcome.trim(),
				repairCompletedByName: values.repairCompletedByName.trim(),
				repairCompletedAt: values.repairCompletedAt,
				repairReportUrl,
			});

			if (!result?.data?.modifyTicket?.ticket) {
				throw new Error('Unable to save the repair report');
			}

			Toast.show('Repair report saved');
			closeDialog();
		}
		catch (error) {
			setFormError(getGraphQLErrorMessage(error, 'Unable to save the repair report'));
		}
		finally {
			setIsSaving(false);
		}
	}, [closeDialog, getServerEndpoint, isSaving, missingRequiredFields.length, modifyItem, pendingReportUrl, selectedReport, ticket.id, ticket.repairReportUrl, values]);

	const hasUploadedReport = isRemoteTicketPhotoUrl(pendingReportUrl || ticket.repairReportUrl);

	return (
		<View style={styles.dialogContainer}>
			<KeyboardAwareScrollView
				style={styles.dialogScroll}
				contentContainerStyle={styles.dialogContent}
				enableOnAndroid={true}
				extraScrollHeight={32}
				keyboardShouldPersistTaps="handled"
				showsVerticalScrollIndicator={true}
			>
				<Text style={styles.dialogTitle}>Complete repair report</Text>
				<Text style={styles.dialogDescription}>
					Complete every required field before resolving this ticket.
				</Text>

				{Boolean(missingRequiredFields.length) && (
					<ErrorCallout
						text={`Required: ${missingRequiredFields.join(', ')}`}
						textStyle={styles.calloutText}
					/>
				)}
				{Boolean(formError) && <ErrorCallout text={formError} textStyle={styles.calloutText} />}

				<Text style={styles.fieldLabel}>Work carried out (required)</Text>
				<TextInput
					multiline={true}
					numberOfLines={4}
					style={styles.multilineInput}
					value={values.repairWorkCarriedOut}
					onChange={updateValue('repairWorkCarriedOut')}
					disabled={isSaving}
				/>

				<Text style={styles.fieldLabel}>Parts used (optional)</Text>
				<TextInput
					multiline={true}
					numberOfLines={3}
					style={styles.multilineInput}
					value={values.repairPartsUsed}
					onChange={updateValue('repairPartsUsed')}
					disabled={isSaving}
				/>

				<Text style={styles.fieldLabel}>Outcome (required)</Text>
				<TextInput
					multiline={true}
					numberOfLines={4}
					style={styles.multilineInput}
					value={values.repairOutcome}
					onChange={updateValue('repairOutcome')}
					disabled={isSaving}
				/>

				<Text style={styles.fieldLabel}>Completed by (required)</Text>
				<TextInput
					value={values.repairCompletedByName}
					onChange={updateValue('repairCompletedByName')}
					disabled={isSaving}
				/>

				<Text style={styles.fieldLabel}>Completed date (required)</Text>
				<DateSelectorInput
					value={values.repairCompletedAt}
					onChange={updateValue('repairCompletedAt')}
					disabled={isSaving}
					placeholder="Select completed date"
				/>

				<Text style={styles.fieldLabel}>Uploaded report (required)</Text>
				<Text style={styles.fileState}>
					{selectedReport?.fileName || (hasUploadedReport ? 'Report uploaded' : 'No report uploaded')}
				</Text>
				<Button
					label={selectedReport || hasUploadedReport ? 'Replace report' : 'Choose report'}
					onPress={chooseReport}
					disabled={isSaving}
					style={styles.fileButton}
				/>
				<Text style={styles.fileGuidance}>PDF, Word document or image. Maximum 10 MB.</Text>
			</KeyboardAwareScrollView>

			<View style={styles.dialogActions}>
				<Button
					label="Cancel"
					onPress={closeDialog}
					disabled={isSaving}
					style={styles.dialogActionButton}
				/>
				<Button
					label={isSaving ? 'Saving…' : 'Save report'}
					onPress={saveReport}
					disabled={isSaving || Boolean(missingRequiredFields.length)}
					loading={isSaving}
					secondary={true}
					style={styles.dialogActionButton}
				/>
			</View>
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	dialogContainer: {
		maxHeight: '82%',
	},
	dialogScroll: {
		flexGrow: 0,
	},
	dialogContent: {
		paddingBottom: space.md,
	},
	dialogTitle: {
		...type.cardTitle,
		color: colors.text,
		marginBottom: 6,
		paddingRight: 28,
	},
	dialogDescription: {
		...type.secondary,
		color: colors.textSecondary,
		marginBottom: space.md,
	},
	calloutText: {
		flex: 1,
		flexShrink: 1,
	},
	fieldLabel: {
		...type.fieldLabel,
		color: colors.text,
		marginBottom: 6,
		marginTop: space.md,
	},
	multilineInput: {
		minHeight: 88,
		maxHeight: 140,
	},
	fileState: {
		...type.bodySmall,
		color: colors.text,
		flexShrink: 1,
		marginBottom: space.sm,
	},
	fileButton: {
		alignSelf: 'flex-start',
	},
	fileGuidance: {
		...type.caption,
		color: colors.textSecondary,
		marginTop: 6,
	},
	dialogActions: {
		flexDirection: 'row',
		gap: space.sm,
		marginTop: space.md,
	},
	dialogActionButton: {
		flex: 1,
	},
});

export default TicketRepairReportDialog;
